// src/components/Sidebar.jsx
import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  BarChart3, Bell, Link as LinkIcon, LogOut, DollarSign,
  User, CodeXmlIcon, Lock, ShoppingCart, LifeBuoy, FileText,
  FileCode2, Ban, Settings2, Globe, Home, ChevronDown,
  ChevronRight, PanelLeftClose, PanelLeftOpen, Package,
  TrendingUp, X,
} from "lucide-react";
import { GoogleAdsLogoOutline } from "./icons/GoogleAdsLogoOutline";
import logo from "../components/assets/images/logo.png";
import toast from "react-hot-toast";
import ThemeToggle from "./ThemeToggle";
import { useSidebar } from "./SidebarContext";

const SIDEBAR_EXPANDED  = "18rem";
const SIDEBAR_COLLAPSED = "5.5rem";

export default function Sidebar() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { mobileOpen, setMobileOpen } = useSidebar();

  const [user, setUser] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const [collapsed, setCollapsed] = useState(() => {
    try {
      if (window.innerWidth < 768) return false;
      return localStorage.getItem("sobevenda_sidebar_collapsed") === "true";
    } catch { return false; }
  });

  useEffect(() => {
    fetch("https://api.sobevenda.com.br/api-proxy/affiliate/auth/me", {
      headers: { Authorization: `Bearer ${localStorage.getItem("sobevenda_token")}` },
    })
      .then((res) => res.json())
      .then((data) => setUser(data))
      .catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (window.innerWidth < 768) setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) { setMobileOpen(false); setCollapsed(false); }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (window.innerWidth >= 768) {
      try { localStorage.setItem("sobevenda_sidebar_collapsed", String(collapsed)); } catch {}
    }
    document.documentElement.style.setProperty("--sidebar-width", collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED);
  }, [collapsed]);

  const handleLogout = () => { localStorage.removeItem("sobevenda_token"); navigate("/login"); };

  if (!user) return null;

  const handleLockedFeature = () => {
    toast.info("Faça upgrade para acessar essa funcionalidade exclusiva dos planos pagos!");
  };

  const isLocked = (path) => {
    const lockedPaths = ["/checkout-links","/google-ads","/sobescripts","/monitoramento-acessos","/blocked-ips"];
    return user.plan === "free" && lockedPaths.includes(path);
  };

  const isActive = (path) => {
  if (path === "/campanhas") {
    return location.pathname === "/campanhas" || location.pathname === "/campanhas/";
  }
  if (path === "/campanhas/analise") {
    // /campanhas/analise OU /campanhas/:id/analise
    return location.pathname === "/campanhas/analise" || /^\/campanhas\/[^/]+\/analise/.test(location.pathname);
  }
  return location.pathname.startsWith(path);
};


  // Light: fundo slate-100, ativo bg-white com sombra leve
  // Dark: fundo slate-900, ativo bg-slate-700
  const itemCls = (path, locked = false) => [
    "group flex items-center rounded-lg transition-all select-none w-full",
    collapsed ? "justify-center px-3 py-3" : "gap-3 px-3 py-2.5",
    isActive(path) && !locked
      ? "bg-white shadow-sm text-slate-900 font-semibold dark:bg-slate-700 dark:shadow-none dark:text-slate-100"
      : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100",
    locked
      ? "opacity-40 cursor-not-allowed hover:bg-transparent hover:text-slate-500 dark:hover:text-slate-400"
      : !isActive(path) ? "hover:bg-white/70 dark:hover:bg-slate-800" : "",
  ].join(" ");

  const sectionTitleCls =
    "px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500";

  const renderNavItem = ({ to, icon, label, locked = false, onClick }) => {
    const content = (
      <>
        <span className="shrink-0">{icon}</span>
        {!collapsed && <span className="text-left leading-tight text-sm">{label}</span>}
        {!collapsed && locked && <Lock className="w-3.5 h-3.5 ml-auto shrink-0 text-slate-400 dark:text-slate-600" />}
      </>
    );

    if (locked) return (
      <button key={to} type="button" className={itemCls(to, true)} disabled onClick={handleLockedFeature} title={collapsed ? label : ""}>
        {content}
      </button>
    );

    if (onClick) return (
      <button key={to} type="button" className={itemCls(to, false)} onClick={onClick} title={collapsed ? label : ""}>
        {content}
      </button>
    );

    return (
      <Link key={to} to={to} className={itemCls(to, false)} title={collapsed ? label : ""}>
        {content}
      </Link>
    );
  };

  const SectionTitle = ({ children }) =>
    collapsed ? null : <div className={sectionTitleCls}>{children}</div>;

  const planLabel = user.plan
    ? `Plano ${user.plan.charAt(0).toUpperCase() + user.plan.slice(1)}`
    : "Central de rastreamento";

  return (
    <>
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/40 z-30 md:hidden backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
      )}

      <aside
        className={`
          fixed left-0 top-0 bottom-0 z-40 flex flex-col h-screen
          bg-slate-100 border-r border-slate-200
          dark:bg-slate-900 dark:border-slate-800
          transition-all duration-300
          md:translate-x-0
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
        `}
        style={{ width: collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED, minWidth: collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED }}
      >
        {/* Topo */}
        <div className={collapsed ? "px-3 py-5" : "px-4 py-5"}>
          <div className={collapsed ? "flex flex-col items-center gap-3" : "flex items-center justify-between gap-3"}>
            <div className={collapsed ? "flex flex-col items-center gap-2" : "flex items-center gap-3"}>
              <div className="rounded-xl bg-white dark:bg-white flex items-center justify-center shrink-0 shadow-sm" style={{ width: 40, height: 40 }}>
                <img src={logo} alt="SobeVenda" className="w-10 h-10 object-contain" />
              </div>
              {!collapsed && (
                <div className="min-w-0">
                  <div className="text-base font-extrabold tracking-tight text-slate-900 dark:text-slate-100">SobeVenda</div>
                  <div className="text-[11px] text-slate-400 dark:text-slate-500">{planLabel}</div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-1.5">
              <button type="button" onClick={() => setMobileOpen(false)}
                className="md:hidden inline-flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-400 hover:bg-white dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-100 transition">
                <X className="w-4 h-4" />
              </button>
              <button type="button" onClick={() => setCollapsed((prev) => !prev)}
                className="hidden md:inline-flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-400 hover:bg-white dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-100 transition"
                title={collapsed ? "Expandir menu" : "Recolher menu"}>
                {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        {/* Nav */}
        <div className={`flex-1 min-h-0 overflow-y-auto sidebar-scroll ${collapsed ? "px-2 pb-2" : "px-3 pb-2"}`}>
          <nav className="flex flex-col gap-5">

            <div>
              <SectionTitle>Começar</SectionTitle>
              <div className="flex flex-col gap-0.5 mt-1">
                {renderNavItem({ to: "/dashboard",            icon: <Home       className="w-5 h-5" />, label: "Início" })}
                {renderNavItem({ to: "/configuracao-inicial", icon: <Settings2  className="w-5 h-5" />, label: "Configuração Inicial" })}
              </div>
            </div>

            <div>
              <SectionTitle>Estrutura</SectionTitle>
              <div className="flex flex-col gap-0.5 mt-1">
                {renderNavItem({ to: "/google-ads",   icon: <GoogleAdsLogoOutline size={20} />, label: "Google Ads",            locked: isLocked("/google-ads")   })}
                {renderNavItem({ to: "/produtos",     icon: <Package     className="w-5 h-5" />, label: "Produtos"                                                 })}
                {renderNavItem({ to: "/domains",      icon: <Globe       className="w-5 h-5" />, label: "Domínios"                                                 })}
                {renderNavItem({ to: "/sobescripts",  icon: <CodeXmlIcon className="w-5 h-5" />, label: "SobeScripts (Tracker)", locked: isLocked("/sobescripts") })}
                {renderNavItem({ to: "/sobepages",    icon: <FileCode2   className="w-5 h-5" />, label: "Páginas"                                                  })}
              </div>
            </div>

            <div>
              <SectionTitle>Resultados</SectionTitle>
              <div className="flex flex-col gap-0.5 mt-1">
                {renderNavItem({ to: "/campanhas",             icon: <TrendingUp className="w-5 h-5" />, label: "Campanhas"                                                       })}
                {renderNavItem({ to: "/campanhas/analise",     icon: <BarChart3  className="w-5 h-5" />, label: "Análise de Campanhas" })}
                {renderNavItem({ to: "/sales",                 icon: <DollarSign className="w-5 h-5" />, label: "Vendas"                                                          })}
                {renderNavItem({ to: "/monitoramento-acessos", icon: <BarChart3  className="w-5 h-5" />, label: "Acessos",     locked: isLocked("/monitoramento-acessos") })}
              </div>
            </div>

            <div>
              <SectionTitle>Ferramentas</SectionTitle>
              <div className="flex flex-col gap-0.5 mt-1">
                {renderNavItem({ to: "/gerador-url",  icon: <LinkIcon  className="w-5 h-5" />, label: "Gerador de URL" })}
                {renderNavItem({ to: "/webhook-logs", icon: <FileText  className="w-5 h-5" />, label: "Logs"           })}
                {renderNavItem({ to: "/support",      icon: <LifeBuoy  className="w-5 h-5" />, label: "Suporte"        })}
              </div>
            </div>

            <div>
              <button type="button" onClick={() => setAdvancedOpen((prev) => !prev)}
                className={collapsed
                  ? "w-full flex items-center justify-center rounded-lg py-3 text-slate-400 hover:bg-white/70 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-100 transition"
                  : "w-full flex items-center justify-between px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 transition"
                }
                title={collapsed ? "Avançado" : ""}>
                {collapsed ? (
                  <ChevronRight className={`w-4 h-4 transition-transform ${advancedOpen ? "rotate-90" : ""}`} />
                ) : (
                  <>
                    <span>Avançado</span>
                    {advancedOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  </>
                )}
              </button>

              {advancedOpen && (
                <div className="mt-0.5 flex flex-col gap-0.5">
                  {renderNavItem({ to: "/notification-links", icon: <Bell         className="w-5 h-5" />, label: "Links de Notificação"                                })}
                  {renderNavItem({ to: "/checkout-links",     icon: <ShoppingCart className="w-5 h-5" />, label: "Links de Checkout", locked: isLocked("/checkout-links") })}
                  {renderNavItem({ to: "/blocked-ips",        icon: <Ban          className="w-5 h-5" />, label: "Bloqueio de IP",    locked: isLocked("/blocked-ips")   })}
                </div>
              )}
            </div>

          </nav>
        </div>

        {/* Rodapé */}
        <div className={`${collapsed ? "px-2" : "px-3"} pb-4 pt-4 border-t border-slate-200 dark:border-slate-800`}>
          <div className="mb-2">
            <ThemeToggle />
          </div>

          <Link to="/account" className={itemCls("/account")} title={collapsed ? "Perfil" : ""}>
            <User className="w-5 h-5 shrink-0" />
            {!collapsed && <span className="text-sm">Perfil</span>}
          </Link>

          <button onClick={handleLogout} title={collapsed ? "Sair" : ""}
            className={[
              "flex items-center w-full mt-1 rounded-lg border border-red-200 dark:border-red-900/50",
              "text-red-500 dark:text-red-400",
              "hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-300 transition",
              collapsed ? "justify-center px-3 py-3" : "gap-3 px-3 py-2.5",
            ].join(" ")}>
            <LogOut className="w-5 h-5 shrink-0" />
            {!collapsed && <span className="text-sm">Sair</span>}
          </button>
        </div>
      </aside>
    </>
  );
}

export function useSidebarToggle() {
  const [mobileOpen, setMobileOpen] = useState(false);
  return { mobileOpen, setMobileOpen };
}